import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    useMediaQuery,
    useTheme,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { Add, Edit, Delete, Search, LocationCity } from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';
import DistrictAutocomplete, { DistrictOption } from '../../components/common/DistrictAutocomplete';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface Mandal {
    id: number;
    name: string;
    district_id: number;
    district?: { id: number; name: string };
    created_at: string;
}

interface MandalFormData {
    name: string;
    district_id: string;
}

const initialFormData: MandalFormData = {
    name: '',
    district_id: '',
};

const Mandals = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedMandal, setSelectedMandal] = useState<Mandal | null>(null);
    const [formData, setFormData] = useState<MandalFormData>(initialFormData);
    // The Autocomplete binds to the district object; formData.district_id stays the
    // id the API expects, so the request payload is unchanged.
    const [selectedDistrict, setSelectedDistrict] = useState<DistrictOption | null>(null);
    const [error, setError] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [mandalToDelete, setMandalToDelete] = useState<Mandal | null>(null);

    // Fetch mandals
    const { data, isLoading } = useQuery({
        queryKey: ['mandals', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;
            const response = await api.get('/superadmin/mandals', { params });
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    // Create/Update mutation
    const saveMutation = useMutation({
        mutationFn: async (data: MandalFormData) => {
            if (selectedMandal) {
                return api.put(`/superadmin/mandals/${selectedMandal.id}`, data);
            }
            return api.post('/superadmin/mandals', data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['mandals'] });
            handleClose();
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/superadmin/mandals/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mandals'] });
        },
        onError: (err: any) => {
            setError(err.response?.data?.message || err.message);
        },
    });

    const handleOpen = (mandal?: Mandal) => {
        if (mandal) {
            setSelectedMandal(mandal);
            setFormData({ name: mandal.name, district_id: String(mandal.district_id || mandal.district?.id || '') });
            // The list endpoint eager-loads the district, so editing needs no extra fetch.
            setSelectedDistrict(mandal.district ? { id: mandal.district.id, name: mandal.district.name } : null);
        } else {
            setSelectedMandal(null);
            setFormData(initialFormData);
            setSelectedDistrict(null);
        }
        setError('');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedMandal(null);
        setFormData(initialFormData);
        setSelectedDistrict(null);
        setError('');
    };

    const handleDistrictChange = (district: DistrictOption | null) => {
        setSelectedDistrict(district);
        setFormData((prev) => ({ ...prev, district_id: district ? String(district.id) : '' }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // An Autocomplete can hold typed text without a selection, so check the id
        // rather than relying on the input's native required validation.
        if (!formData.district_id) {
            setError('Please select a district');
            return;
        }
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (mandal: Mandal) => {
        setMandalToDelete(mandal);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (mandalToDelete) {
            deleteMutation.mutate(mandalToDelete.id);
        }
        setDeleteDialogOpen(false);
        setMandalToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setMandalToDelete(null);
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        {
            field: 'district',
            headerName: 'District',
            flex: 1,
            minWidth: 150,
            valueGetter: (value, row) => row.district?.name || 'N/A'
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 120,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    // Mobile columns - fewer columns for smaller screens
    const mobileColumns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        {
            field: 'actions',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton size="small" onClick={() => handleOpen(params.row)}>
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
                        <Delete fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const rows = data?.data?.data || data?.data || [];
    const rowCount = data?.data?.total || data?.total || 0;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h2">Mandals</Typography>
            </Box>

            {/* Search */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <TextField
                        sx={{ width: '30%', borderRadius: 1 }}
                        size="small"
                        placeholder="Search mandals..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button variant="contained" startIcon={<LocationCity />} onClick={() => handleOpen()}>
                        Add Mandal
                    </Button>
                </CardContent>
            </Card>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}


            {/* DataGrid */}
            <Card>
                <DataGrid
                    rows={rows}
                    columns={isMobile ? mobileColumns : columns}
                    loading={isLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={rowCount}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>

            {/* Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedMandal ? 'Edit Mandal' : 'Add Mandal'}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <TextField
                            label="Mandal Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />
                        <DistrictAutocomplete
                            value={selectedDistrict}
                            onChange={handleDistrictChange}
                            required
                            // Don't fetch districts until the dialog is actually open.
                            enabled={open}
                        />
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{mandalToDelete?.name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Mandals;
